import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { DataTable } from "@/components/ui/data-table";
import { TableCard } from "@/components/ui/table-card";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ReactSelect, AsyncReactSelect } from "@/components/ui/react-select";
import { toast } from "sonner";
import {
  CreditCard,
  PlusCircle,
  Edit,
  Trash2,
  MoreVertical,
  Save,
  GripVertical,
  ArrowUpDown,
  X,
} from "lucide-react";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// ─── Constants ────────────────────────────────────────────────────────────────
const BLANK_FORM = {
  id: "",
  code: "",
  name: "",
  is_active: true,
  usable_in_sales: true,
  usable_in_expenses: true,
  usable_in_refunds: true,
  usable_in_purchases: false,
  settlement_type: "immediate",
  method_group: "personal",
  sort_order: 0,
  store_options: [], // full { value, label } objects
};

// ─── Draggable Row ────────────────────────────────────────────────────────────
const DraggableRow = ({ method }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: method.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-card border rounded-md mb-2 shadow-sm"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none flex-shrink-0"
        type="button"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <CreditCard className="h-5 w-5 text-muted-foreground" />
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{method.name}</span>
          <span className="text-xs text-muted-foreground">{method.code}</span>
        </div>
      </div>
      <div className="flex-shrink-0 mr-4">
        <Badge variant={method.is_active ? "success" : "destructive"}>
          {method.is_active ? "Active" : "Inactive"}
        </Badge>
      </div>
    </div>
  );
};

// ─── Form Dialog ──────────────────────────────────────────────────────────────
function PaymentMethodFormDialog({
  open,
  onClose,
  onSubmit,
  editMode,
  form,
  setForm,
  isPending,
}) {
  const loadStoreOptions = useCallback(async (inputValue) => {
    try {
      const res = await apiClient.get("/store", {
        params: { search: inputValue, perPage: 20 },
      });
      return (res.data?.data ?? []).map((s) => ({ value: s.id, label: s.name }));
    } catch {
      return [];
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            {editMode ? "Edit Payment Method" : "Add Payment Method"}
          </DialogTitle>
          <DialogDescription>
            {editMode
              ? "Update the payment method details below."
              : "Fill in the details to create a new payment method."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="pm-name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="pm-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Mobile Money"
              maxLength={255}
            />
          </div>

          {/* Code */}
          <div className="space-y-1.5">
            <Label htmlFor="pm-code">
              Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="pm-code"
              value={form.code}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  code: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                }))
              }
              placeholder="e.g. mobile_money"
              maxLength={50}
              disabled={editMode && form.is_system}
            />
            <p className="text-[11px] text-muted-foreground">
              Unique identifier, lowercase with underscores.
            </p>
          </div>

          {/* Group + Settlement */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Method Group</Label>
              <Select
                value={form.method_group}
                onValueChange={(v) => setForm((f) => ({ ...f, method_group: v }))}
              >
                <SelectTrigger id="pm-group">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Settlement</Label>
              <Select
                value={form.settlement_type}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, settlement_type: v }))
                }
              >
                <SelectTrigger id="pm-settlement">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="delayed">Delayed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Capabilities */}
          <div className="space-y-2">
            <Label>Usable in</Label>
            <div className="rounded-lg border border-border divide-y divide-border">
              {[
                {
                  key: "usable_in_sales",
                  label: "Sales",
                  desc: "Can be selected when processing a sale or invoice",
                },
                {
                  key: "usable_in_expenses",
                  label: "Expenses",
                  desc: "Can be selected when recording an expense",
                },
                {
                  key: "usable_in_purchases",
                  label: "Purchases",
                  desc: "Can be selected when recording a purchase",
                },
                {
                  key: "usable_in_refunds",
                  label: "Refunds",
                  desc: "Can be used when issuing a refund to a customer",
                },
              ].map(({ key, label, desc }) => (
                <label
                  key={key}
                  htmlFor={`pm-${key}`}
                  className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-muted/40 transition-colors first:rounded-t-lg last:rounded-b-lg"
                >
                  <Checkbox
                    id={`pm-${key}`}
                    checked={form[key]}
                    onCheckedChange={(checked) =>
                      setForm((f) => ({ ...f, [key]: Boolean(checked) }))
                    }
                    className="mt-0.5 shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground leading-none mb-0.5">
                      {label}
                    </p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="space-y-1.5">
            <Label>Status</Label>
            <ReactSelect
              options={[
                { value: true, label: "Active" },
                { value: false, label: "Inactive" },
              ]}
              value={
                form.is_active
                  ? { value: true, label: "Active" }
                  : { value: false, label: "Inactive" }
              }
              onChange={(opt) =>
                setForm((f) => ({ ...f, is_active: opt?.value ?? true }))
              }
            />
          </div>

          {/* Stores */}
          <div className="space-y-1.5">
            <Label>Stores</Label>
            <AsyncReactSelect
              isMulti
              loadOptions={loadStoreOptions}
              defaultOptions
              value={form.store_options}
              onChange={(opts) =>
                setForm((f) => ({ ...f, store_options: Array.isArray(opts) ? opts : [] }))
              }
              placeholder="Search stores…"
              isClearable
              cacheOptions
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isPending}>
            {isPending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Saving…
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {editMode ? "Update" : "Save"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const PaymentMethods = () => {
  const queryClient = useQueryClient();

  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Reorder state
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Use the store already selected in the global header
  const currentStoreId = useSelector((state) => state.current_store?.id ?? null);

  // ── Query ─────────────────────────────────────────────────────────────────
  const { data: methods = [], isLoading } = useQuery({
    queryKey: ["payment-methods", currentStoreId],
    queryFn: async () => {
      const params = { perPage: 100 };
      if (currentStoreId) params.store_id = currentStoreId;
      const res = await apiClient.get("/payment-methods", { params });
      return res.data?.data ?? [];
    },
    enabled: hasPermission("payment-method read"),
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const { id, ...body } = payload;
      if (id) return apiClient.put(`/payment-methods/${id}`, body);
      return apiClient.post("/payment-methods", body);
    },
    onSuccess: (_, payload) => {
      toast.success(
        `Payment method ${payload.id ? "updated" : "created"} successfully!`,
      );
      queryClient.invalidateQueries({ queryKey: ["payment-methods"], refetchType: "all" });
      setModalOpen(false);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to save payment method",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => apiClient.delete(`/payment-methods/${id}`),
    onSuccess: () => {
      toast.success("Payment method deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["payment-methods"], refetchType: "all" });
      setDeleteTarget(null);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to delete payment method",
      );
      setDeleteTarget(null);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items) => {
      const promises = items.map((item, index) => {
        const originalItem = methods.find((m) => m.id === item.id);
        if (originalItem && originalItem.sort_order !== index) {
          return apiClient.put(`/payment-methods/${item.id}`, {
            ...item,
            store_ids: item.stores || [],
            sort_order: index,
          });
        }
        return Promise.resolve();
      });
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-methods"], refetchType: "all" });
      toast.success("Payment method order saved successfully!");
      setReorderMode(false);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to save order",
      );
    },
  });

  React.useEffect(() => {
    if (reorderMode) {
      setReorderItems([...methods].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    }
  }, [reorderMode, methods]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setForm({ ...BLANK_FORM });
    setEditMode(false);
    setModalOpen(true);
  };

  const handleEdit = async (method) => {
    // Resolve store UUIDs to { value, label } by fetching from the API
    let store_options = [];
    if (method.stores?.length) {
      try {
        const res = await apiClient.get("/store", { params: { perPage: 100 } });
        const allStores = res.data?.data ?? [];
        store_options = method.stores
          .map((id) => {
            const found = allStores.find((s) => s.id === id);
            return found ? { value: found.id, label: found.name } : null;
          })
          .filter(Boolean);
      } catch {
        // fall back to showing IDs if fetch fails
        store_options = method.stores.map((id) => ({ value: id, label: id }));
      }
    }
    setForm({
      id: method.id,
      code: method.code,
      name: method.name,
      is_active: Boolean(method.is_active),
      usable_in_sales: Boolean(method.usable_in_sales),
      usable_in_expenses: Boolean(method.usable_in_expenses),
      usable_in_refunds: Boolean(method.usable_in_refunds),
      usable_in_purchases: Boolean(method.usable_in_purchases),
      settlement_type: method.settlement_type ?? "immediate",
      method_group: method.method_group ?? "personal",
      sort_order: method.sort_order ?? 0,
      store_options,
      is_system: method.is_system,
    });
    setEditMode(true);
    setModalOpen(true);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.code.trim()) { toast.error("Code is required"); return; }

    if (editMode && !hasPermission("payment-method update")) {
      toast.error("You do not have permission to update payment methods.");
      return;
    }
    if (!editMode && !hasPermission("payment-method create")) {
      toast.error("You do not have permission to create payment methods.");
      return;
    }

    const { store_options, ...rest } = form;
    saveMutation.mutate({
      ...rest,
      store_ids: Array.isArray(store_options) ? store_options.map((o) => o.value) : [],
    });
  };

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setReorderItems((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleCancelReorder = () => {
    setReorderMode(false);
    setReorderItems([]);
  };

  const handleSaveReorder = () => {
    reorderMutation.mutate(reorderItems);
  };

  // ── Table data ────────────────────────────────────────────────────────────
  const filtered = methods
    .filter((m) =>
      Object.values(m).some((v) =>
        String(v ?? "").toLowerCase().includes(searchText.toLowerCase()),
      ),
    )
    .sort((a, b) => a.sort_order - b.sort_order);

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div>
          <p className="font-medium text-foreground">{row.original.name}</p>
          <p className="text-xs text-muted-foreground">
            {row.original.code}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "method_group",
      header: "Group",
      cell: ({ getValue }) => (
        <span className="capitalize text-sm">{getValue() || "-"}</span>
      ),
    },
    {
      accessorKey: "settlement_type",
      header: "Settlement",
      cell: ({ getValue }) => (
        <span className="capitalize text-sm">{getValue() || "-"}</span>
      ),
    },
    {
      id: "usable_in",
      header: "Usable in",
      cell: ({ row }) => {
        const m = row.original;
        const uses = [
          m.usable_in_sales && "Sales",
          m.usable_in_expenses && "Expenses",
          m.usable_in_purchases && "Purchases",
          m.usable_in_refunds && "Refunds",
        ].filter(Boolean);
        return (
          <span className="text-sm text-muted-foreground">
            {uses.length ? uses.join(", ") : "None"}
          </span>
        );
      },
    },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? "success" : "destructive"}>
          {row.original.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const m = row.original;
        if (m.is_system) {
          return (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5">
              System Protected
            </Badge>
          );
        }

        const canEdit = hasPermission("payment-method update");
        const canDelete = hasPermission("payment-method delete");

        if (!canEdit && !canDelete) return null;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canEdit && (
                <DropdownMenuItem onClick={() => handleEdit(m)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {canEdit && canDelete && <DropdownMenuSeparator />}
              {canDelete && (
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setDeleteTarget(m)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Payment Methods"
          subtitle="Manage how payments are accepted across your stores"
        />
        <PageHeaderActions>
          {reorderMode ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancelReorder}
                disabled={reorderMutation.isPending}
              >
                <X className="mr-2 h-4 w-4" /> Cancel
              </Button>
              <Button
                onClick={handleSaveReorder}
                disabled={reorderMutation.isPending}
              >
                {reorderMutation.isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" /> Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" /> Save Order
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {hasPermission("payment-method update") && (
                <Button variant="outline" onClick={() => setReorderMode(true)}>
                  <ArrowUpDown className="mr-2 h-4 w-4" /> Reorder
                </Button>
              )}
              {hasPermission("payment-method create") && (
                <Button onClick={handleAdd}>
                  <PlusCircle className="mr-2 h-4 w-4" /> Add Method
                </Button>
              )}
            </>
          )}
        </PageHeaderActions>
      </PageHeader>

      {reorderMode ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/40 rounded-md border border-dashed">
            <GripVertical className="h-4 w-4 flex-shrink-0" />
            <span>
              Drag the <GripVertical className="inline h-3 w-3" /> handle to
              reorder. Click <strong>Save Order</strong> when done.
            </span>
          </div>
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={reorderItems.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <div>
                {reorderItems.map((method) => (
                  <DraggableRow key={method.id} method={method} />
                ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeId ? (
                <div className="flex items-center gap-3 p-3 bg-card border-2 border-primary rounded-md shadow-lg opacity-90">
                  <GripVertical className="h-4 w-4 text-primary flex-shrink-0" />
                  <CreditCard className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">
                    {reorderItems.find((i) => i.id === activeId)?.name}
                  </span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      ) : (
        <>
          <FilterCard>
            <FilterSearch
              placeholder="Search payment methods…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </FilterCard>

          <TableCard>
            <DataTable
              columns={columns}
              data={filtered}
              isLoading={isLoading}
              footerInfo={
                <span className="font-medium text-foreground">
                  Total: {filtered.length}
                </span>
              }
            />
          </TableCard>
        </>
      )}

      {/* Create / Edit Dialog */}
      <PaymentMethodFormDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        editMode={editMode}
        form={form}
        setForm={setForm}
        isPending={saveMutation.isPending}
      />

      {/* Delete Confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <strong>{deleteTarget?.name}</strong>. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? (
                <Spinner className="h-4 w-4" />
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default PaymentMethods;
